import { useState, useEffect, useCallback } from "react";
import { Heart, Zap, Lightbulb } from "lucide-react";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

const REACTIONS = [
  { key: "this_matters", icon: Heart, label: "This matters", activeColor: "text-rose-400", activeBg: "bg-rose-500/15 border-rose-500/30" },
  { key: "surprising", icon: Zap, label: "Surprising", activeColor: "text-amber-400", activeBg: "bg-amber-500/15 border-amber-500/30" },
  { key: "tell_me_more", icon: Lightbulb, label: "Insightful", activeColor: "text-cyan-400", activeBg: "bg-cyan-500/15 border-cyan-500/30" },
];

export const ReactionButtons = ({ insightId }) => {
  const [counts, setCounts] = useState({});
  const [reacted, setReacted] = useState({});

  // Load persisted reactions from localStorage
  useEffect(() => {
    try {
      const stored = JSON.parse(localStorage.getItem("aioo_reactions") || "{}");
      if (stored[insightId]) setReacted(stored[insightId]);
    } catch {}
  }, [insightId]);

  // Fetch counts from backend
  const fetchCounts = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/reactions/counts`);
      if (res.data[insightId]) setCounts(res.data[insightId]);
    } catch {}
  }, [insightId]);

  useEffect(() => { fetchCounts(); }, [fetchCounts]);

  const handleReaction = async (reactionKey) => {
    if (reacted[reactionKey]) return;

    // Optimistic update
    setCounts((prev) => ({ ...prev, [reactionKey]: (prev[reactionKey] || 0) + 1 }));
    const newReacted = { ...reacted, [reactionKey]: true };
    setReacted(newReacted);

    // Persist to localStorage
    try {
      const stored = JSON.parse(localStorage.getItem("aioo_reactions") || "{}");
      stored[insightId] = newReacted;
      localStorage.setItem("aioo_reactions", JSON.stringify(stored));
    } catch {}

    // Fire to backend
    try {
      await axios.post(`${API}/reactions`, { insight_id: insightId, reaction: reactionKey });
    } catch {}
  };

  return (
    <div className="flex items-center gap-2 flex-wrap" data-testid={`reactions-${insightId}`}>
      {REACTIONS.map(({ key, icon: Icon, label, activeColor, activeBg }) => {
        const isActive = reacted[key];
        const count = counts[key] || 0;
        return (
          <button
            key={key}
            onClick={() => handleReaction(key)}
            disabled={isActive}
            data-testid={`reaction-${key}-${insightId}`}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium border transition-all duration-200 ${
              isActive
                ? `${activeBg} ${activeColor}`
                : "bg-white/[0.03] border-white/[0.08] text-muted-foreground hover:bg-white/[0.06] hover:border-white/[0.15]"
            }`}
          >
            <Icon className={`w-3.5 h-3.5 ${isActive ? activeColor : ""}`} />
            {label}
            {count > 0 && (
              <span className={`ml-0.5 tabular-nums ${isActive ? activeColor : "text-muted-foreground/60"}`}>
                {count}
              </span>
            )}
          </button>
        );
      })}
    </div>
  );
};

export default ReactionButtons;
